import React, { useState, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/AdminNotificationModal.css';

interface Notification {
  id: string;
  type: string;
  request_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  request_name: string;
  edited_at: string;
  read: boolean;
  company_id?: string;
  changes?: Change[];  // Add changes field
}

interface Change {
  field: string;
  old_value: any;
  new_value: any;
  field_label: string;
}

interface RequestDetails {
  _id: string;
  product_name: string;
  product_brand?: string;
  product_global_code?: string;
  product_company_code?: string;
  product_description?: string;
  quantity?: number;
  unit?: string;
  budget?: number;
  currency?: string;
  delivery_time?: string;
  importance?: string;
  region?: string[];
  category?: string;
  current_supplier?: string;
  favorite_suppliers?: string[];
  additional_specifications?: string[];
  custom_specifications?: any[];
  unit_price?: number;
  total_price?: number;
  urgency?: string;
  current_price?: number;
  additional_notes?: string;
  custom_filters?: any;
  is_ocr_generated?: boolean;
  ocr_data?: any;
  ocr_prices?: any;
  ocr_supplier_info?: any;
  status?: string;
  phase?: string;
  created_at?: string;
  updated_at?: string;
  user_name?: string;
  user_email?: string;
  edited_by?: string;
  edited_at?: string;
}

interface AdminNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminNotificationModal: React.FC<AdminNotificationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RequestDetails | null>(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(API_ENDPOINTS.ADMIN_NOTIFICATIONS, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setNotifications(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleViewRequest = async (requestId: string) => {
    setRequestLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

              const response = await fetch(API_ENDPOINTS.REQUEST_DETAIL(requestId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        setSelectedRequest(data.data);
        setShowRequestModal(true);
      } else {
        alert('Failed to load request details');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      alert('Error loading request details');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    // TODO: Implement mark as read functionality
    console.log('Mark as read:', notificationId);
  };

  const closeRequestModal = () => {
    setShowRequestModal(false);
    setSelectedRequest(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="admin-notification-modal-overlay" onClick={onClose}>
        <div className="admin-notification-modal" onClick={(e) => e.stopPropagation()}>
          <div className="admin-notification-modal-header">
            <h2>{t('common.admin_notifications')}</h2>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          
          <div className="admin-notification-modal-content">
            {loading ? (
              <div className="loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                {t('common.no_notifications')}
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map((notification) => (
                  <div key={notification.id} className={`notification-item ${!notification.read ? 'unread' : ''}`}>
                    <div className="notification-header">
                      <div className="notification-user">
                        <strong>{notification.user_name}</strong>
                        <span className="user-email">({notification.user_email})</span>
                      </div>
                      <div className="notification-time">
                        {formatDate(notification.edited_at)}
                      </div>
                    </div>
                    
                    <div className="notification-content">
                      <p>
                        {t('common.edited_request')}: <strong>{notification.request_name}</strong>
                      </p>
                      
                      {/* Show changes if available */}
                      {notification.changes && notification.changes.length > 0 && (
                        <div className="changes-summary">
                          <p className="changes-title">
                            <strong>{t('common.changes_made')}:</strong>
                          </p>
                          <div className="changes-list">
                            {notification.changes.map((change, index) => (
                              <div key={index} className="change-item">
                                <span className="change-field">{change.field_label}:</span>
                                <span className="change-old">"{change.old_value || 'Empty'}"</span>
                                <span className="change-arrow">→</span>
                                <span className="change-new">"{change.new_value || 'Empty'}"</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="notification-actions">
                      <button 
                        className="view-request-btn"
                        onClick={() => handleViewRequest(notification.request_id)}
                        disabled={requestLoading}
                      >
                        {requestLoading ? 'Loading...' : t('common.view_request')}
                      </button>
                      {!notification.read && (
                        <button 
                          className="mark-read-btn"
                          onClick={() => handleMarkAsRead(notification.id)}
                        >
                          {t('common.mark_as_read')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Details Modal */}
      {showRequestModal && selectedRequest && (
        <div className="request-details-modal-overlay" onClick={closeRequestModal}>
          <div className="request-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="request-details-modal-header">
              <h2>{t('common.request_details')}</h2>
              <button className="close-btn" onClick={closeRequestModal}>×</button>
            </div>
            
            <div className="request-details-modal-content">
              <div className="request-info-grid">
                <div className="info-item">
                  <label>{t('common.product_name')}:</label>
                  <span>{selectedRequest.product_name}</span>
                </div>
                
                {selectedRequest.product_brand && (
                  <div className="info-item">
                    <label>{t('common.product_brand')}:</label>
                    <span>{selectedRequest.product_brand}</span>
                  </div>
                )}
                
                {selectedRequest.product_global_code && (
                  <div className="info-item">
                    <label>{t('common.product_global_code')}:</label>
                    <span>{selectedRequest.product_global_code}</span>
                  </div>
                )}
                
                {selectedRequest.product_company_code && (
                  <div className="info-item">
                    <label>{t('common.product_company_code')}:</label>
                    <span>{selectedRequest.product_company_code}</span>
                  </div>
                )}
                
                {selectedRequest.product_description && (
                  <div className="info-item full-width">
                    <label>{t('common.product_description')}:</label>
                    <span>{selectedRequest.product_description}</span>
                  </div>
                )}
                
                {selectedRequest.quantity && (
                  <div className="info-item">
                    <label>{t('common.quantity')}:</label>
                    <span>{selectedRequest.quantity} {selectedRequest.unit}</span>
                  </div>
                )}
                
                {selectedRequest.budget && (
                  <div className="info-item">
                    <label>{t('common.budget')}:</label>
                    <span>{selectedRequest.budget} {selectedRequest.currency}</span>
                  </div>
                )}
                
                {selectedRequest.unit_price && (
                  <div className="info-item">
                    <label>{t('common.unit_price')}:</label>
                    <span>{selectedRequest.unit_price} {selectedRequest.currency}</span>
                  </div>
                )}
                
                {selectedRequest.total_price && (
                  <div className="info-item">
                    <label>{t('common.total_price')}:</label>
                    <span>{selectedRequest.total_price} {selectedRequest.currency}</span>
                  </div>
                )}
                
                {selectedRequest.delivery_time && (
                  <div className="info-item">
                    <label>{t('common.delivery_time')}:</label>
                    <span>{selectedRequest.delivery_time}</span>
                  </div>
                )}
                
                {selectedRequest.importance && (
                  <div className="info-item">
                    <label>{t('common.importance')}:</label>
                    <span>{selectedRequest.importance}</span>
                  </div>
                )}
                
                {selectedRequest.urgency && (
                  <div className="info-item">
                    <label>{t('common.urgency')}:</label>
                    <span>{selectedRequest.urgency}</span>
                  </div>
                )}
                
                {selectedRequest.region && selectedRequest.region.length > 0 && (
                  <div className="info-item">
                    <label>{t('common.region')}:</label>
                    <span>{selectedRequest.region.join(', ')}</span>
                  </div>
                )}
                
                {selectedRequest.category && (
                  <div className="info-item">
                    <label>{t('common.category')}:</label>
                    <span>{selectedRequest.category}</span>
                  </div>
                )}
                
                {selectedRequest.current_supplier && (
                  <div className="info-item">
                    <label>{t('common.current_supplier')}:</label>
                    <span>{selectedRequest.current_supplier}</span>
                  </div>
                )}
                
                {selectedRequest.current_price && (
                  <div className="info-item">
                    <label>{t('common.current_price')}:</label>
                    <span>{selectedRequest.current_price} {selectedRequest.currency}</span>
                  </div>
                )}
                
                {selectedRequest.favorite_suppliers && selectedRequest.favorite_suppliers.length > 0 && (
                  <div className="info-item full-width">
                    <label>{t('common.favorite_suppliers')}:</label>
                    <span>{selectedRequest.favorite_suppliers.join(', ')}</span>
                  </div>
                )}
                
                {selectedRequest.additional_specifications && selectedRequest.additional_specifications.length > 0 && (
                  <div className="info-item full-width">
                    <label>{t('common.additional_specifications')}:</label>
                    <span>{selectedRequest.additional_specifications.join(', ')}</span>
                  </div>
                )}
                
                {selectedRequest.custom_specifications && selectedRequest.custom_specifications.length > 0 && (
                  <div className="info-item full-width">
                    <label>{t('common.custom_specifications')}:</label>
                    <div className="custom-specs-list">
                      {selectedRequest.custom_specifications.map((spec, index) => (
                        <div key={index} className="custom-spec-item">
                          <strong>{spec.key}:</strong> {spec.value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedRequest.additional_notes && (
                  <div className="info-item full-width">
                    <label>{t('common.additional_notes')}:</label>
                    <span>{selectedRequest.additional_notes}</span>
                  </div>
                )}
                
                {selectedRequest.status && (
                  <div className="info-item">
                    <label>{t('common.status')}:</label>
                    <span className={`status-badge status-${selectedRequest.status}`}>
                      {selectedRequest.status}
                    </span>
                  </div>
                )}
                
                {selectedRequest.phase && (
                  <div className="info-item">
                    <label>{t('common.phase')}:</label>
                    <span className={`phase-badge phase-${selectedRequest.phase}`}>
                      {selectedRequest.phase}
                    </span>
                  </div>
                )}
                
                {selectedRequest.is_ocr_generated !== undefined && (
                  <div className="info-item">
                    <label>{t('common.is_ocr_generated')}:</label>
                    <span>{selectedRequest.is_ocr_generated ? t('common.yes') : t('common.no')}</span>
                  </div>
                )}
                
                {selectedRequest.created_at && (
                  <div className="info-item">
                    <label>{t('common.created_at')}:</label>
                    <span>{formatDate(selectedRequest.created_at)}</span>
                  </div>
                )}
                
                {selectedRequest.updated_at && (
                  <div className="info-item">
                    <label>{t('common.updated_at')}:</label>
                    <span>{formatDate(selectedRequest.updated_at)}</span>
                  </div>
                )}
                
                {selectedRequest.edited_at && (
                  <div className="info-item">
                    <label>{t('common.edited_at')}:</label>
                    <span>{formatDate(selectedRequest.edited_at)}</span>
                  </div>
                )}
                
                {selectedRequest.edited_by && (
                  <div className="info-item">
                    <label>{t('common.edited_by')}:</label>
                    <span>{selectedRequest.edited_by}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminNotificationModal; 